import * as configureWevoTypes from "./types";
import { normalize } from "normalizr";
import * as apiHelper from "../../helpers/api";
import { imageSchema, pageSchema, wevoSchema } from "../../schemas";
import { requestWithAuth } from "../../utils/request";
import { errorActionFactory as error } from "../../helpers/errors";
import { trackerActions } from "../../modules/tracker";
import { ConfigureProjectEvents } from "../../modules/tracker/event-names";
import { isNil } from "lodash";

const API_URL = apiHelper.getApiUrl();

export function requestStateClearForKey(removalPath) {
    return {
        type: configureWevoTypes.CLEAR_STATUS_STATE,
        removalPath
    };
}

/*
 Wevo Creation & Modification Action Broadcasts
 */
function requestCreateWevo() {
    return {
        type: configureWevoTypes.REQUEST_CREATE_WEVO
    };
}

function successfulCreateWevo(wevoId, entities) {
    return {
        type: configureWevoTypes.CREATE_WEVO_SUCCESS,
        entities,
        wevoId
    };
}

function requestSaveWevo(wevoId) {
    return {
        type: configureWevoTypes.REQUEST_SAVE_WEVO,
        wevoId
    };
}

/*
 Page Retrieval, Creation & Modification Action Broadcasts
 */

function requestNewPage(sortOrder, name) {
    return {
        type: configureWevoTypes.REQUEST_CREATE_PAGE,
        sortOrder,
        name
    };
}

function successfulPageCreate(wevoId, pageId, sortOrder, entities) {
    return {
        type: configureWevoTypes.CREATE_PAGE_SUCCESS,
        entities,
        wevoId,
        pageId,
        sortOrder
    };
}

function successfulImageUpload(entities) {
    return {
        type: configureWevoTypes.IMAGE_UPLOAD_SUCCESS,
        entities
    };
}

function requestPage() {
    return {
        type: configureWevoTypes.REQUEST_RETRIEVE_PAGE
    };
}

function successfulRequestPage(entities) {
    return {
        type: configureWevoTypes.RETRIEVE_PAGE_SUCCESS,
        entities
    };
}

function requestPageUpdate(pageId) {
    return {
        type: configureWevoTypes.REQUEST_UPDATE_PAGE,
        pageId
    };
}

function successfulRemovePage(pageId, entities) {
    return {
        type: configureWevoTypes.REMOVE_PAGE_SUCCESS,
        pageId,
        entities
    };
}

function successfulPageUpdate(entities, pageId) {
    return {
        type: configureWevoTypes.UPDATE_PAGE_SUCCESS,
        entities,
        pageId
    };
}

function removePageErrors(activeUploadIndex) {
    return {
        type: configureWevoTypes.REMOVE_SELECTED_PAGE_ERROR_CARD,
        activeUploadIndex
    };
}

/*
 CTA Creation & Modification Action Broadcasts
 */

function requestCreateCta(wevoId) {
    return {
        type: configureWevoTypes.REQUEST_CREATE_CTA,
        wevoId
    };
}

function successfulCreateCta(entities, wevoId) {
    return {
        type: configureWevoTypes.CREATE_CTA_SUCCESS,
        wevoId,
        entities
    };
}

function requestRemoveCta(wevoId) {
    return {
        type: configureWevoTypes.REQUEST_REMOVE_CTA,
        wevoId
    };
}

function successfulRemoveCta(entities, wevoId) {
    return {
        type: configureWevoTypes.REMOVE_CTA_SUCCESS,
        entities,
        wevoId
    };
}

/*
 Audience Actions
 */

function requestAttachAudienceToWevo(wevoId, audienceId) {
    return {
        type: configureWevoTypes.REQUEST_ATTACH_AUDIENCE,
        wevoId,
        audienceId
    };
}

function successfulAttachAudienceToWevo(entities, wevoId, audienceId) {
    return {
        type: configureWevoTypes.ATTACH_AUDIENCE_SUCCESS,
        entities,
        wevoId,
        audienceId
    };
}

function requestDetachAudienceFromWevo(wevoId, audienceId) {
    return {
        type: configureWevoTypes.REQUEST_DETACH_AUDIENCE,
        wevoId,
        audienceId
    };
}

function successfulDetachAudience(entities, wevoId, audienceId) {
    return {
        type: configureWevoTypes.DETACH_AUDIENCE_SUCCESS,
        wevoId,
        audienceId,
        entities
    };
}

function requestAudienceSegmentUpdate(wevoId, audienceId, segmentId) {
    return {
        type: configureWevoTypes.REQUEST_UPDATE_AUDIENCE_SEGMENT,
        wevoId,
        audienceId,
        segmentId
    };
}

function successfulAudienceSegmentUpdate(
    entities,
    wevoId,
    audienceId,
    segmentId
) {
    return {
        type: configureWevoTypes.UPDATE_AUDIENCE_SEGMENT_SUCCESS,
        entities,
        wevoId,
        audienceId,
        segmentId
    };
}

/*
 Wevo Functions
 */

export function createWevo(teamId, wevoCreationObject) {
    return dispatch => {
        const trackerObject = {
            team_id: teamId,
            ...wevoCreationObject
        };
        dispatch(
            trackerActions.trackEvent(
                ConfigureProjectEvents.DID_ATTEMPT_PROJECT_CREATION,
                trackerObject
            )
        );
        dispatch(requestCreateWevo());
        return requestWithAuth(dispatch, http =>
            http.post(`${API_URL}/teams/${teamId}/wevos`, wevoCreationObject)
        )
            .then(({ data }) => {
                const { entities } = normalize(data, wevoSchema);
                dispatch(
                    trackerActions.trackEvent(
                        ConfigureProjectEvents.DID_CREATE_PROJECT_SUCCESSFULLY,
                        trackerObject
                    )
                );
                return dispatch(successfulCreateWevo(data.id, entities));
            })
            .catch(err => {
                dispatch(
                    trackerActions.trackEvent(
                        ConfigureProjectEvents.DID_RECEIVE_PROJECT_DEFINITION_ERROR,
                        {
                            ...trackerObject,
                            error_message: err.toString()
                        }
                    )
                );
                return dispatch(
                    error(configureWevoTypes.CREATE_WEVO_ERROR, err)
                );
            });
    };
}

export function updateWevo(wevoId, wevoEditObject) {
    return dispatch => {
        const trackerObject = {
            wevo_id: wevoId,
            ...wevoEditObject
        };
        dispatch(
            trackerActions.trackEvent(
                ConfigureProjectEvents.DID_ATTEMPT_PROJECT_EDIT,
                trackerObject
            )
        );
        dispatch(requestSaveWevo(wevoId));
        return requestWithAuth(dispatch, http =>
            http.post(`${API_URL}/wevos/${wevoId}`, wevoEditObject)
        )
            .then(({ data }) => {
                const { entities } = normalize(data, wevoSchema);
                dispatch(
                    trackerActions.trackEvent(
                        ConfigureProjectEvents.DID_EDIT_PROJECT_SUCCESSFULLY,
                        trackerObject
                    )
                );
                dispatch({
                    type: configureWevoTypes.SAVE_WEVO_SUCCESS,
                    entities,
                    wevoId: wevoId
                });
            })
            .catch(err => {
                dispatch(
                    trackerActions.trackEvent(
                        ConfigureProjectEvents.DID_RECEIVE_PROJECT_DEFINITION_ERROR,
                        {
                            ...trackerObject,
                            error_message: err.toString()
                        }
                    )
                );
                dispatch(
                    error(configureWevoTypes.SAVE_WEVO_ERROR, err, { wevoId })
                );
            });
    };
}

/*
 Page functions
 */

export function fetchPage(wevoId, pageId) {
    return dispatch => {
        dispatch(requestPage());
        return requestWithAuth(dispatch, http =>
            http.get(`${API_URL}/wevos/${wevoId}/pages/${pageId}`)
        )
            .then(({ data }) => {
                const { entities } = normalize(data, pageSchema);
                dispatch(successfulRequestPage(entities));
            })
            .catch(err =>
                dispatch(error(configureWevoTypes.RETRIEVE_PAGE_ERROR, err))
            );
    };
}

function uploadImage(dispatch, file) {
    const formData = new FormData();
    formData.append("file", file, file.name);
    return requestWithAuth(dispatch, http =>
        http.post(`${API_URL}/images`, formData)
    )
        .then(({ data }) => {
            const { entities } = normalize(data, imageSchema);
            dispatch(successfulImageUpload(entities));
            return data.id;
        })
        .catch(err => {
            dispatch(error(configureWevoTypes.IMAGE_UPLOAD_ERROR, err, file));
            throw err;
        });
}

export function createPageFromImage(file, wevoId, deviceId, sortOrder) {
    return dispatch => {
        const fileName =
            file.name.indexOf(".") > -1 ? file.name.split(".")[0] : file.name;
        dispatch(requestNewPage(sortOrder, fileName));
        const trackerObject = {
            page_name: fileName,
            sort_order: sortOrder,
            device_id: deviceId
        };
        dispatch(
            trackerActions.trackEvent(
                ConfigureProjectEvents.DID_ATTEMPT_PAGE_CREATION,
                trackerObject
            )
        );
        const onError = err => {
            dispatch(
                trackerActions.trackEvent(
                    ConfigureProjectEvents.DID_RECEIVE_SELECT_PAGES_ERROR,
                    {
                        ...trackerObject,
                        error_message: err.toString()
                    }
                )
            );
            dispatch(
                error(configureWevoTypes.CREATE_PAGE_ERROR, err, {
                    sortOrder,
                    name: fileName
                })
            );
            throw err;
        };
        return uploadImage(dispatch, file)
            .then(imageId => {
                const pageCreationObject = {
                    imageId,
                    name: fileName,
                    sortOrder,
                    deviceId
                };
                return requestWithAuth(
                    dispatch,
                    http =>
                        http.post(
                            `${API_URL}/wevos/${wevoId}/pages`,
                            pageCreationObject
                        ),
                    { onError }
                );
            })
            .then(({ data }) => {
                const { entities } = normalize(data, pageSchema);
                dispatch(
                    trackerActions.trackEvent(
                        ConfigureProjectEvents.DID_CREATE_PAGE_SUCCESSFULLY,
                        trackerObject
                    )
                );
                return dispatch(
                    successfulPageCreate(wevoId, data.id, sortOrder, entities)
                );
            })
            .catch(onError);
    };
}

export function clearErrorFromSelectPages(activeUploadIndex) {
    return dispatch => dispatch(removePageErrors(activeUploadIndex));
}

export function clearAllErrorsFromSelectPages() {
    return dispatch => dispatch(removePageErrors());
}

export function removePageFromWevo(wevoId, pageId) {
    return dispatch => {
        const trackerObject = {
            wevo_id: wevoId,
            page_id: pageId
        };
        dispatch(
            trackerActions.trackEvent(
                ConfigureProjectEvents.DID_ATTEMPT_PAGE_REMOVAL,
                trackerObject
            )
        );
        return requestWithAuth(dispatch, http =>
            http.delete(`${API_URL}/wevos/${wevoId}/pages/${pageId}`)
        )
            .then(({ data }) => {
                const { entities } = normalize(data, pageSchema);
                dispatch(
                    trackerActions.trackEvent(
                        ConfigureProjectEvents.DID_REMOVE_PAGE_SUCCESSFULLY,
                        trackerObject
                    )
                );
                return dispatch(successfulRemovePage(pageId, entities));
            })
            .catch(err => {
                dispatch(
                    trackerActions.trackEvent(
                        ConfigureProjectEvents.DID_RECEIVE_SELECT_PAGES_ERROR,
                        {
                            ...trackerObject,
                            error_message: err.toString()
                        }
                    )
                );
                dispatch(
                    error(configureWevoTypes.REMOVE_PAGE_ERROR, err, { pageId })
                );
                throw err;
            });
    };
}

export function updatePage(wevoId, pageId, properties) {
    return dispatch => {
        const trackerObject = {
            page_id: pageId,
            ...properties
        };
        if (!isNil(properties.isBaseline)) {
            dispatch(
                trackerActions.trackEvent(
                    properties.isBaseline
                        ? ConfigureProjectEvents.DID_ATTEMPT_UNMARK_BASELINE
                        : ConfigureProjectEvents.DID_ATTEMPT_MARK_BASELINE,
                    trackerObject
                )
            );
        }
        if (!isNil(properties.isCompetitor)) {
            dispatch(
                trackerActions.trackEvent(
                    properties.isCompetitor
                        ? ConfigureProjectEvents.DID_ATTEMPT_UNMARK_COMPETITOR
                        : ConfigureProjectEvents.DID_ATTEMPT_MARK_COMPETITOR,
                    trackerObject
                )
            );
        }
        if (!isNil(properties.name)) {
            dispatch(
                trackerActions.trackEvent(
                    ConfigureProjectEvents.DID_ATTEMPT_EDIT_PAGE_NAME,
                    trackerObject
                )
            );
        }
        dispatch(requestPageUpdate(pageId));
        return requestWithAuth(dispatch, http =>
            http.post(`${API_URL}/wevos/${wevoId}/pages/${pageId}`, properties)
        )
            .then(({ data }) => {
                const { entities } = normalize(data, pageSchema);
                dispatch(
                    trackerActions.trackEvent(
                        ConfigureProjectEvents.DID_EDIT_PAGE_SUCCESSFULLY,
                        trackerObject
                    )
                );
                return dispatch(successfulPageUpdate(entities, pageId));
            })
            .catch(err => {
                dispatch(
                    trackerActions.trackEvent(
                        ConfigureProjectEvents.DID_RECEIVE_SELECT_PAGES_ERROR,
                        {
                            ...trackerObject,
                            error_message: err.toString()
                        }
                    )
                );
                dispatch(
                    error(configureWevoTypes.UPDATE_PAGE_ERROR, err, { pageId })
                );
                throw err;
            });
    };
}

/*
 CTA functions
 */

export function addCta(
    wevoId,
    pageId,
    { x, y, clickNumber, width, height, label }
) {
    return dispatch => {
        const trackerObject = {
            wevo_id: wevoId,
            page_id: pageId,
            x,
            y
        };
        dispatch(
            trackerActions.trackEvent(
                ConfigureProjectEvents.DID_ATTEMPT_CTA_CREATION,
                trackerObject
            )
        );
        dispatch(requestCreateCta(wevoId));
        const createCtaObject = {
            x,
            y,
            clickNumber,
            width: width || 100,
            height: height || 100,
            label:
                label ||
                `CTA number ${clickNumber} on wevo ${wevoId} and page ${pageId}`
        };
        return requestWithAuth(dispatch, http =>
            http.post(
                `${API_URL}/wevos/${wevoId}/pages/${pageId}/ctas`,
                createCtaObject
            )
        )
            .then(({ data }) => {
                const { entities } = normalize(data, pageSchema);
                dispatch(
                    trackerActions.trackEvent(
                        ConfigureProjectEvents.DID_CREATE_CTA_SUCCESSFULLY,
                        trackerObject
                    )
                );
                dispatch(successfulCreateCta(entities, wevoId));
            })
            .catch(err => {
                dispatch(
                    trackerActions.trackEvent(
                        ConfigureProjectEvents.DID_RECEIVE_PICK_CTA_ERROR,
                        {
                            ...trackerObject,
                            error_message: err.toString()
                        }
                    )
                );
                dispatch(
                    error(configureWevoTypes.CREATE_CTA_ERROR, err, {
                        wevoId,
                        pageId
                    })
                );
            });
    };
}

export function removeCta(wevoId, pageId, ctaId) {
    return dispatch => {
        const trackerObject = {
            wevo_id: wevoId,
            page_id: pageId,
            cta_id: ctaId
        };
        dispatch(
            trackerActions.trackEvent(
                ConfigureProjectEvents.DID_ATTEMPT_CTA_REMOVAL,
                trackerObject
            )
        );
        dispatch(requestRemoveCta(wevoId));
        return requestWithAuth(dispatch, http =>
            http.delete(
                `${API_URL}/wevos/${wevoId}/pages/${pageId}/ctas/${ctaId}`
            )
        )
            .then(({ data }) => {
                const { entities } = normalize(data, pageSchema);
                dispatch(
                    trackerActions.trackEvent(
                        ConfigureProjectEvents.DID_REMOVE_CTA_SUCCESSFULLY,
                        trackerObject
                    )
                );
                dispatch(successfulRemoveCta(entities, wevoId));
            })
            .catch(err => {
                dispatch(
                    trackerActions.trackEvent(
                        ConfigureProjectEvents.DID_RECEIVE_PICK_CTA_ERROR,
                        {
                            ...trackerObject,
                            error_message: err.toString()
                        }
                    )
                );
                dispatch(
                    error(configureWevoTypes.REMOVE_CTA_ERROR, err, {
                        wevoId,
                        pageId,
                        ctaId
                    })
                );
            });
    };
}

/*
 Audience functions
 */
export function attachAudienceToWevo(wevoId, audienceId) {
    return dispatch => {
        const trackerObject = {
            wevo_id: wevoId,
            audience_id: audienceId
        };
        dispatch(
            trackerActions.trackEvent(
                ConfigureProjectEvents.DID_ATTEMPT_AUDIENCE_ATTACH,
                trackerObject
            )
        );
        dispatch(requestAttachAudienceToWevo(wevoId, audienceId));
        return requestWithAuth(dispatch, http =>
            http.post(`${API_URL}/wevos/${wevoId}/audiences/${audienceId}`)
        )
            .then(({ data }) => {
                const { entities } = normalize(data, wevoSchema);
                dispatch(
                    trackerActions.trackEvent(
                        ConfigureProjectEvents.DID_ATTACH_AUDIENCE_SUCCESSFULLY,
                        trackerObject
                    )
                );
                dispatch(
                    successfulAttachAudienceToWevo(entities, wevoId, audienceId)
                );
            })
            .catch(err => {
                dispatch(
                    trackerActions.trackEvent(
                        ConfigureProjectEvents.DID_RECEIVE_SELECT_AUDIENCE_ERROR,
                        {
                            ...trackerObject,
                            error_message: err.toString()
                        }
                    )
                );
                dispatch(
                    error(configureWevoTypes.ATTACH_AUDIENCE_ERROR, err, {
                        audienceId,
                        wevoId
                    })
                );
            });
    };
}

export function detachAudienceFromWevo(wevoId, audienceId) {
    return dispatch => {
        const trackerObject = {
            wevo_id: wevoId,
            audience_id: audienceId
        };
        dispatch(
            trackerActions.trackEvent(
                ConfigureProjectEvents.DID_ATTEMPT_AUDIENCE_DETACH,
                trackerObject
            )
        );
        dispatch(requestDetachAudienceFromWevo(wevoId, audienceId));
        return requestWithAuth(dispatch, http =>
            http.delete(`${API_URL}/wevos/${wevoId}/audiences/${audienceId}`)
        )
            .then(({ data }) => {
                const { entities } = normalize(data, wevoSchema);
                dispatch(
                    trackerActions.trackEvent(
                        ConfigureProjectEvents.DID_DETACH_AUDIENCE_SUCCESSFULLY,
                        trackerObject
                    )
                );
                dispatch(
                    successfulDetachAudience(entities, wevoId, audienceId)
                );
            })
            .catch(err => {
                dispatch(
                    trackerActions.trackEvent(
                        ConfigureProjectEvents.DID_RECEIVE_SELECT_AUDIENCE_ERROR,
                        {
                            ...trackerObject,
                            error_message: err.toString()
                        }
                    )
                );
                dispatch(
                    error(configureWevoTypes.DETACH_AUDIENCE_ERROR, err, {
                        audienceId,
                        wevoId
                    })
                );
            });
    };
}

export function updateAudienceSegments(wevoId, audienceId, segments) {
    return dispatch => {
        const trackerObject = {
            wevo_id: wevoId,
            audience_id: audienceId,
            segments: segments
        };
        dispatch(
            trackerActions.trackEvent(
                ConfigureProjectEvents.DID_ATTEMPT_SEGMENT_EDIT,
                trackerObject
            )
        );
        dispatch(requestAudienceSegmentUpdate(wevoId, audienceId, segments));
        return requestWithAuth(dispatch, http =>
            http.post(
                `${API_URL}/wevos/${wevoId}/audiences/${audienceId}/segments`,
                segments
            )
        )
            .then(({ data }) => {
                const { entities } = normalize(data, wevoSchema);
                dispatch(
                    trackerActions.trackEvent(
                        ConfigureProjectEvents.DID_EDIT_SEGMENT_SUCCESSFULLY,
                        trackerObject
                    )
                );
                dispatch(
                    successfulAudienceSegmentUpdate(
                        entities,
                        wevoId,
                        audienceId,
                        segments
                    )
                );
            })
            .catch(err => {
                dispatch(
                    trackerActions.trackEvent(
                        ConfigureProjectEvents.DID_RECEIVE_SELECT_AUDIENCE_ERROR
                    ),
                    {
                        ...trackerObject,
                        error_message: err.toString()
                    }
                );
                dispatch(
                    error(
                        configureWevoTypes.UPDATE_AUDIENCE_SEGMENT_ERROR,
                        err,
                        {
                            wevoId,
                            audienceId,
                            segments
                        }
                    )
                );
            });
    };
}

export function toggleQuotaEditorForAudience(wevoId, audienceId) {
    return {
        type: configureWevoTypes.TOGGLE_QUOTA_EDITOR_FOR_AUDIENCE,
        wevoId,
        audienceId
    };
}



// WEBPACK FOOTER //
// ./src/modules/configure-wevo/actions.js